package it.maicol07.gamerlogue.`data`

import it.maicol07.gamerlogue.ui.views.library.GameLibraryStatus
import it.maicol07.spraypaintkt.CollectionProxy
import it.maicol07.spraypaintkt.RecordProxy
import it.maicol07.spraypaintkt.Resource
import it.maicol07.spraypaintkt.ResourceRegistry
import it.maicol07.spraypaintkt.ResourceSerializer
import it.maicol07.spraypaintkt.Scope
import it.maicol07.spraypaintkt.SortDirection
import it.maicol07.spraypaintkt.extensions.DirtyMap
import it.maicol07.spraypaintkt.extensions.trackChanges
import it.maicol07.spraypaintkt.interfaces.JsonApiConfig
import it.maicol07.spraypaintkt.registerResources
import kotlin.Any
import kotlin.Boolean
import kotlin.Int
import kotlin.Number
import kotlin.String
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableMap
import kotlin.reflect.KClass
import kotlinx.serialization.Serializable

@Serializable(LibraryEntry.Companion.Serializer::class)
public class LibraryEntry : Resource, LibraryEntrySchema {
  override val `companion`: Resource.CompanionObj<LibraryEntry> = Companion

  override var id: String? = null

  override var isPersisted: Boolean = false

  override val attributes: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val relationships: DirtyMap<String, Any?> = mutableMapOf<String, Any?>().trackChanges()

  override val meta: MutableMap<String, Any?> = mutableMapOf()

  override val links: MutableMap<String, Any?> = mutableMapOf()

  override val type: String by lazy { companion.resourceType }

  override var gameId: Int
    get() = if (attributes.containsKey("game_id")) {
      attributes["game_id"] as Int
    } else {
      throw NoSuchElementException("gameId not found")
    }
    set(`value`) {
      attributes["game_id"] = value
    }

  override var status: GameLibraryStatus
    get() = if (attributes.containsKey("status")) {
      GameLibraryStatus.valueOf(attributes["status"] as String)
    } else {
      throw NoSuchElementException("status not found")
    }
    set(`value`) {
      attributes["status"] = value.name
    }

  override var completionStatus: LibraryEntrySchema.CompletionStatus?
    get() = if (attributes.containsKey("completion_status")) {
      (attributes["completion_status"] as String?)?.let { LibraryEntrySchema.CompletionStatus.valueOf(it) }
    } else {
      null
    }
    set(`value`) {
      attributes["completion_status"] = value?.name
    }

  override var owned: Boolean
    get() = if (attributes.containsKey("owned")) {
      attributes["owned"] as Boolean
    } else {
      throw NoSuchElementException("owned not found")
    }
    set(`value`) {
      attributes["owned"] = value
    }

  override var editionsIds: List<Int>
    get() = if (attributes.containsKey("editions_ids")) {
      attributes["editions_ids"] as List<Int>
    } else {
      throw NoSuchElementException("editionsIds not found")
    }
    set(`value`) {
      attributes["editions_ids"] = value
    }

  override var platformsIds: List<Int>
    get() = if (attributes.containsKey("platforms_ids")) {
      attributes["platforms_ids"] as List<Int>
    } else {
      throw NoSuchElementException("platformsIds not found")
    }
    set(`value`) {
      attributes["platforms_ids"] = value
    }

  override var startDate: String?
    get() = if (attributes.containsKey("start_date")) {
      attributes["start_date"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["start_date"] = value
    }

  override var endDate: String?
    get() = if (attributes.containsKey("end_date")) {
      attributes["end_date"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["end_date"] = value
    }

  override var playedTime: Int?
    get() = if (attributes.containsKey("played_time")) {
      attributes["played_time"] as Int?
    } else {
      null
    }
    set(`value`) {
      attributes["played_time"] = value
    }

  override var rating: Number?
    get() = if (attributes.containsKey("rating")) {
      attributes["rating"] as Number?
    } else {
      null
    }
    set(`value`) {
      attributes["rating"] = value
    }

  override var ratingDetails: Any?
    get() = if (attributes.containsKey("rating_details")) {
      attributes["rating_details"] as Any?
    } else {
      null
    }
    set(`value`) {
      attributes["rating_details"] = value
    }

  override var review: String?
    get() = if (attributes.containsKey("review")) {
      attributes["review"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["review"] = value
    }

  override var reviewSpoiler: Boolean
    get() = if (attributes.containsKey("review_spoiler")) {
      attributes["review_spoiler"] as Boolean
    } else {
      super.reviewSpoiler
    }
    set(`value`) {
      attributes["review_spoiler"] = value
    }

  override var reviewUpdatedAt: String?
    get() = if (attributes.containsKey("review_updated_at")) {
      attributes["review_updated_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["review_updated_at"] = value
    }

  override var userId: String?
    get() = if (attributes.containsKey("user_id")) {
      attributes["user_id"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["user_id"] = value
    }

  override var likesCount: Int
    get() = if (attributes.containsKey("likes_count")) {
      attributes["likes_count"] as Int
    } else {
      super.likesCount
    }
    set(`value`) {
      attributes["likes_count"] = value
    }

  override var commentsCount: Int
    get() = if (attributes.containsKey("comments_count")) {
      attributes["comments_count"] as Int
    } else {
      super.commentsCount
    }
    set(`value`) {
      attributes["comments_count"] = value
    }

  override var likedByMe: Boolean
    get() = if (attributes.containsKey("liked_by_me")) {
      attributes["liked_by_me"] as Boolean
    } else {
      super.likedByMe
    }
    set(`value`) {
      attributes["liked_by_me"] = value
    }

  override var createdAt: String?
    get() = if (attributes.containsKey("created_at")) {
      attributes["created_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["created_at"] = value
    }

  override var updatedAt: String?
    get() = if (attributes.containsKey("updated_at")) {
      attributes["updated_at"] as String?
    } else {
      null
    }
    set(`value`) {
      attributes["updated_at"] = value
    }

  override var user: User?
    get() = if (relationships.containsKey("user") && (relationships["user"] == null || relationships["user"] is User?)) {
      relationships["user"] as User?
    } else {
      null
    }
    set(`value`) {
      relationships["user"] = value
    }

  public constructor(
    gameId: Int? = null,
    status: GameLibraryStatus? = null,
    completionStatus: LibraryEntrySchema.CompletionStatus? = null,
    owned: Boolean? = null,
    editionsIds: List<Int>? = null,
    platformsIds: List<Int>? = null,
    startDate: String? = null,
    endDate: String? = null,
    playedTime: Int? = null,
    rating: Number? = null,
    ratingDetails: Any? = null,
    review: String? = null,
    reviewSpoiler: Boolean? = null,
    reviewUpdatedAt: String? = null,
    userId: String? = null,
    likesCount: Int? = null,
    commentsCount: Int? = null,
    likedByMe: Boolean? = null,
    createdAt: String? = null,
    updatedAt: String? = null,
    user: User? = null,
  ) {
    if (gameId != null) {
      attributes["game_id"] = gameId
    }
    if (status != null) {
      attributes["status"] = status.name
    }
    if (completionStatus != null) {
      attributes["completion_status"] = completionStatus.name
    }
    if (owned != null) {
      attributes["owned"] = owned
    }
    if (editionsIds != null) {
      attributes["editions_ids"] = editionsIds
    }
    if (platformsIds != null) {
      attributes["platforms_ids"] = platformsIds
    }
    if (startDate != null) {
      attributes["start_date"] = startDate
    }
    if (endDate != null) {
      attributes["end_date"] = endDate
    }
    if (playedTime != null) {
      attributes["played_time"] = playedTime
    }
    if (rating != null) {
      attributes["rating"] = rating
    }
    if (ratingDetails != null) {
      attributes["rating_details"] = ratingDetails
    }
    if (review != null) {
      attributes["review"] = review
    }
    if (reviewSpoiler != null) {
      attributes["review_spoiler"] = reviewSpoiler
    }
    if (reviewUpdatedAt != null) {
      attributes["review_updated_at"] = reviewUpdatedAt
    }
    if (userId != null) {
      attributes["user_id"] = userId
    }
    if (likesCount != null) {
      attributes["likes_count"] = likesCount
    }
    if (commentsCount != null) {
      attributes["comments_count"] = commentsCount
    }
    if (likedByMe != null) {
      attributes["liked_by_me"] = likedByMe
    }
    if (createdAt != null) {
      attributes["created_at"] = createdAt
    }
    if (updatedAt != null) {
      attributes["updated_at"] = updatedAt
    }
    if (user != null) {
      relationships["user"] = user
    }
  }

  public companion object : Resource.CompanionObj<LibraryEntry> {
    override val resourceType: String = "LibraryEntry"

    override val endpoint: String = "library_entries"

    override val config: JsonApiConfig = AppJsonApiConfig

    override val factory: () -> LibraryEntry = { LibraryEntry() }

    override val schema: KClass<LibraryEntrySchema> = LibraryEntrySchema::class

    init {
      ResourceRegistry.registerResources()
    }

    public fun scope(options: Scope<LibraryEntry>.() -> Unit = {}): Scope<LibraryEntry> = Scope(LibraryEntry::class, options)

    public final suspend fun all(): CollectionProxy<LibraryEntry> = scope().all()

    public final suspend fun find(id: String): RecordProxy<LibraryEntry> = scope().find(id)

    public final suspend fun findOrNull(id: String): RecordProxy<LibraryEntry?> = scope().findOrNull(id)

    public final suspend fun first(): RecordProxy<LibraryEntry> = scope().first()

    public final suspend fun firstOrNull(): RecordProxy<LibraryEntry?> = scope().firstOrNull()

    public final suspend fun exists(): Boolean = scope().exists()

    public final suspend fun last(): RecordProxy<LibraryEntry> = scope().last()

    public final suspend fun lastOrNull(): RecordProxy<LibraryEntry?> = scope().lastOrNull()

    public final fun page(pageNumber: Number): Scope<LibraryEntry> = scope().page(pageNumber)

    public final fun per(pageSize: Number): Scope<LibraryEntry> = scope().per(pageSize)

    public final fun limit(limit: Number): Scope<LibraryEntry> = scope().limit(limit)

    public final fun offset(offset: Number): Scope<LibraryEntry> = scope().offset(offset)

    public final fun `where`(attribute: String, `value`: Any): Scope<LibraryEntry> = scope().where(attribute, value)

    public final fun extraParam(key: String, `value`: String): Scope<LibraryEntry> = scope().extraParam(key, value)

    public final fun order(attribute: String): Scope<LibraryEntry> = scope().order(attribute)

    public final fun order(attribute: String, sortDirection: SortDirection): Scope<LibraryEntry> = scope().order(attribute, sortDirection)

    public final fun includes(vararg relationships: String): Scope<LibraryEntry> = scope().includes(*relationships)

    public final fun select(type: String, vararg fields: String): Scope<LibraryEntry> = scope().select(type, *fields)

    public object Serializer : ResourceSerializer<LibraryEntry>()
  }
}
